import type { Clip, Project, Track, TrackFreeze } from './types';
import { clipEndTicks, effectivePlaybackRate } from './selectors';
import { readSamplerZones } from '@/engine/instruments/samplerState';

/**
 * Track freezing.
 *
 * A frozen track plays one pre-rendered file instead of running its clips, its
 * instrument and its inserts. That is the standard answer to a project that has
 * outgrown the CPU: a twelve-voice synth with three reverbs costs the same as a
 * single audio clip once it is frozen.
 *
 * Nothing is destroyed. The clips, the instrument and the effects stay exactly
 * where they were; the freeze is one extra record on the track, and unfreezing
 * is discarding it. The rendered audio is registered in `project.assets` like
 * any other import, so it saves, packages and garbage-collects with no special
 * handling.
 *
 * The render stops at the fader. Volume, pan, mute, solo and sends stay live,
 * which is what makes a frozen track still mixable — freezing is a CPU
 * decision, and it should not also be a mixing decision.
 */

/**
 * Fingerprints everything the frozen render depended on.
 *
 * Editing a frozen track is allowed — refusing edits would be a worse trade
 * than a stale render — so instead the freeze carries a signature and the UI
 * says when it no longer matches. Playing audio that silently disagrees with
 * what is on screen is the one outcome worth ruling out.
 *
 * Deliberately excluded: the track's own volume, pan, mute, solo and sends,
 * because the render stops before them.
 *
 * Tracks feeding a frozen *bus* are the exception to "everything about other
 * tracks". Their material is inside the render, so an edit to one of them makes
 * the bus's freeze just as wrong as an edit to the bus itself — and unlike the
 * bus, their faders are inside it too, since a bus hears its feeders post-fader.
 */
export function freezeSignature(project: Project, trackId: string): string {
  const track = project.tracks[trackId];
  if (!track) return '';

  const feeders = feedersOf(project, trackId)
    .slice()
    .sort()
    .map((id) => {
      const feeder = project.tracks[id];
      if (!feeder) return null;
      return {
        i: id,
        // A bus hears its feeders through their faders, so those are part of
        // what was rendered.
        v: [feeder.volume, feeder.pan, feeder.mute, feeder.solo, feeder.outputTrackId],
        // A frozen feeder contributes its render, not its clips.
        z: feeder.freeze?.assetId ?? null,
        s: trackSignature(project, id),
      };
    });

  return JSON.stringify({ self: trackSignature(project, trackId), feeders });
}

/** The part of the fingerprint that describes one track's own material. */
function trackSignature(project: Project, trackId: string): unknown {
  const track = project.tracks[trackId];
  if (!track) return null;

  const clips = track.clipIds
    .map((id) => project.clips[id])
    .filter((clip): clip is Clip => Boolean(clip))
    .sort((a, b) => a.startTicks - b.startTicks || a.id.localeCompare(b.id))
    .map((clip) => clipFingerprint(project, clip));

  const automation = track.automationLaneIds
    .map((id) => project.automation[id])
    .filter(Boolean)
    .map((lane) => ({
      t: lane.target,
      e: lane.enabled,
      p: lane.points.map((point) => [point.tick, Number(point.value.toFixed(6)), point.curve]),
    }));

  return {
    // The tempo map decides where every clip lands in time.
    tempo: project.transport.tempoMap,
    ppq: project.transport.ppq,
    clips,
    effects: track.effects,
    instrument: track.instrument,
    automation,
  };
}

function clipFingerprint(project: Project, clip: Clip): unknown {
  const base = {
    i: clip.id,
    s: clip.startTicks,
    m: clip.muted,
    l: clip.takeLane,
  };
  if (clip.kind === 'midi') {
    return {
      ...base,
      d: clip.durationTicks,
      lo: [clip.loop, clip.loopLengthTicks],
      n: clip.notes.map((note) => [note.pitch, note.velocity, note.startTicks, note.durationTicks]),
    };
  }
  return {
    ...base,
    a: clip.assetId,
    o: Number(clip.offsetSec.toFixed(9)),
    d: Number(clip.durationSec.toFixed(9)),
    g: clip.gain,
    ge: clip.gainEnvelope.map((point) => [Number(point.sourceSec.toFixed(9)), point.gain, point.curve]),
    fi: [clip.fadeIn.durationSec, clip.fadeIn.curve, clip.fadeIn.curvature],
    fo: [clip.fadeOut.durationSec, clip.fadeOut.curve, clip.fadeOut.curvature],
    // The *effective* rate, so retuning the project tempo makes a frozen track
    // holding warped clips stale — the render no longer matches what plays.
    r: [Number(effectivePlaybackRate(project, clip).toFixed(9)), clip.reversed, clip.preservePitch],
    // A shift is baked into the render, so a frozen track holding a clip whose
    // transposition changed is as stale as one whose speed did. So is the
    // formant position, which changes the samples without changing the shift.
    p: [clip.pitchSemitones, clip.formantSemitones],
  };
}

/** True when a track is frozen and its render still matches what is on it. */
export function isFreezeCurrent(project: Project, trackId: string): boolean {
  const freeze = project.tracks[trackId]?.freeze;
  if (!freeze) return false;
  return freeze.signature === freezeSignature(project, trackId);
}

/** True when a track is frozen but has been edited since. */
export function isFreezeStale(project: Project, trackId: string): boolean {
  const freeze = project.tracks[trackId]?.freeze;
  return Boolean(freeze) && !isFreezeCurrent(project, trackId);
}

/**
 * Whether the frozen render should be played instead of the track's own
 * material. A stale freeze still plays: the alternative is a track that goes
 * silent, or one that suddenly costs full CPU again, neither of which is what
 * the user asked for by making one small edit.
 */
export function playsFrozen(project: Project, track: Track): boolean {
  return Boolean(track.freeze && project.assets[track.freeze.assetId]);
}

/** Every track whose output eventually reaches `trackId`. */
export function feedersOf(project: Project, trackId: string): string[] {
  const out: string[] = [];
  for (const id of project.trackOrder) {
    let current = project.tracks[id]?.outputTrackId ?? null;
    for (let guard = 0; guard < 32 && current; guard++) {
      if (current === trackId) {
        out.push(id);
        break;
      }
      current = project.tracks[current]?.outputTrackId ?? null;
    }
  }
  return out;
}

/**
 * True when a track's own sound is already inside a frozen bus downstream.
 *
 * Freezing a bus renders everything feeding it, so those tracks must fall silent
 * or the render and the live material would both play — the doubling that makes
 * a freeze sound wrong rather than merely stale.
 */
export function silencedByFrozenBus(project: Project, trackId: string): boolean {
  let current = project.tracks[trackId]?.outputTrackId ?? null;
  for (let guard = 0; guard < 32 && current; guard++) {
    const track = project.tracks[current];
    if (!track) return false;
    if (playsFrozen(project, track)) return true;
    current = track.outputTrackId;
  }
  return false;
}

/** The span a freeze should cover: everything the track has material for. */
export function freezeRange(project: Project, trackId: string): { startTicks: number; endTicks: number } {
  const track = project.tracks[trackId];
  if (!track) return { startTicks: 0, endTicks: 0 };

  const ends = (id: string): number => {
    let end = 0;
    for (const clipId of project.tracks[id]?.clipIds ?? []) {
      const clip = project.clips[clipId];
      // Alternate takes are not heard, so they are not rendered either.
      if (!clip || clip.takeLane !== 0) continue;
      end = Math.max(end, clipEndTicks(project, clip));
    }
    return end;
  };

  let end = ends(trackId);
  // A bus has no material of its own: what it plays is whatever feeds it, so
  // that is what has to be covered. Without this a frozen bus would render zero
  // seconds and fall silent, which looks like the freeze breaking the mix.
  for (const feeder of feedersOf(project, trackId)) end = Math.max(end, ends(feeder));
  return { startTicks: 0, endTicks: Math.ceil(end) };
}

/**
 * How many channels a track's own signal has.
 *
 * This matters more than it looks. `StereoPannerNode` applies a constant-power
 * law to *mono* input — 0.707 in each channel at centre — but passes *stereo*
 * input through untouched. So a freeze rendered at the wrong width comes back
 * 3dB louder or quieter than the track it replaced, purely because the pan law
 * changed underneath it.
 *
 * The width cannot be recovered from the rendered samples: a mono source
 * up-mixed by the render context looks exactly like stereo with identical
 * channels. It can be computed from the project, and so it is.
 */
export function trackChannelCount(project: Project, trackId: string): number {
  const track = project.tracks[trackId];
  if (!track) return 2;

  // Anything arriving from another track has already been through that track's
  // panner, and a panner's output is stereo whatever went into it. So a bus is
  // stereo the moment something feeds it, however mono the sources are — render
  // it mono and it comes back 3dB down, because the bus's own panner then
  // applies the constant-power law a second time.
  if (feedersOf(project, trackId).length > 0) return 2;

  let channels = 1;
  for (const clipId of track.clipIds) {
    const clip = project.clips[clipId];
    if (!clip || clip.kind !== 'audio' || clip.takeLane !== 0) continue;
    channels = Math.max(channels, project.assets[clip.assetId]?.channelCount ?? 1);
  }

  const instrument = track.instrument;
  if (instrument?.type === 'sampler') {
    for (const zone of readSamplerZones(instrument.data)) {
      channels = Math.max(channels, project.assets[zone.assetId]?.channelCount ?? 1);
    }
  }

  return Math.min(2, Math.max(1, channels));
}

/** Tracks whose frozen audio the project still needs. */
export function frozenAssetIds(project: Project): string[] {
  const ids: string[] = [];
  for (const track of Object.values(project.tracks)) {
    if (track.freeze) ids.push(track.freeze.assetId);
  }
  return ids;
}

export function describeFreeze(freeze: TrackFreeze, stale: boolean): string {
  const when = new Date(freeze.createdAt).toLocaleTimeString();
  return stale ? `Frozen at ${when} — edited since` : `Frozen at ${when}`;
}
